import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
    Button, Box, Text, Flex, Select, Input, Checkbox, Divider,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useBIMStore } from '../../state/useBIMStore';
import { useAppStore } from '../../state/store';
import { FilterRule, Schedule, ScheduleField } from '../../core/types';

/** Element categories that commonly carry schedules. */
const SCHEDULE_CATEGORIES = [
    'Walls', 'Doors', 'Windows', 'Floors', 'Roofs', 'Ceilings',
    'Structural Columns', 'Structural Framing', 'Stairs',
    'Rooms', 'Spaces', 'Mechanical Equipment', 'Lighting Fixtures',
    'Precast Walls', 'Precast Floors', 'Precast Columns', 'Precast Beams',
    'Precast Mounting Parts', 'Precast Reinforcement',
];

/** Default fields offered per schedule creation. */
const DEFAULT_FIELDS = ['Type', 'Level', 'Width', 'Height', 'Area', 'Volume', 'Name'];

/**
 * Schedule dialog (BIM: View > Schedules > Schedule/Quantities).
 *
 * Creates schedules bound to an element category with a field list, stores
 * them in the project (persisted with the document), and renders the live
 * table computed from the current model. Existing schedules are listed and
 * can be opened or deleted. Store-driven like the other Manage dialogs:
 * visibility comes from appStore.manageDialog === 'schedules'.
 */
export function ScheduleDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.manageDialog === 'schedules');
    const onClose = useAppStore((s) => s.closeManageDialog);
    const project = useBIMStore((s) => s.project);
    const createSchedule = useBIMStore((s) => s.createSchedule);
    const removeSchedule = useBIMStore((s) =>s.removeSchedule); const [category, setCategory] = useState(SCHEDULE_CATEGORIES[0]); const [name, setName] = useState(''); const [fields, setFields] = useState<string[]>(['Type', 'Name']); const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

    const schedules = useMemo(() => Array.from(project.schedules.values()), [project.schedules]);
    const activeSchedule = selectedScheduleId ? project.schedules.get(selectedScheduleId) : null;

    /** Rows for a stored schedule, computed straight from the model elements. */
    const rows = useMemo(() => {
        if (!activeSchedule) return [];
        const elements = Array.from(project.elements.values()).filter(
            (el) => el.category === activeSchedule.categoryId
        );
        let filtered = elements.filter((el) =>
            activeSchedule.filters.every((rule: FilterRule) => {
                const v = (el.parameters as Record<string, unknown>)?.[rule.parameter]
                    ?? (rule.parameter === 'Category' ? el.category : rule.parameter === 'Type' ? el.type : undefined);
                if (v === undefined) return false;
                switch (rule.evaluator) {
                    case 'equals': return String(v) === String(rule.value);
                    case 'greater': return Number(v) >Number(rule.value); case 'less': return Number(v)< Number(rule.value);
                    case 'contains': return String(v).toLowerCase().includes(String(rule.value).toLowerCase());
                    case 'beginsWith': return String(v).toLowerCase().startsWith(String(rule.value).toLowerCase());
                    default: return true;
                }
            })
        );
        for (const sort of [...activeSchedule.sorting].reverse()) {
            const field = activeSchedule.fields.find((f) => f.parameterName === sort.field);
            const key = field?.parameterName ?? sort.field;
            filtered = [...filtered].sort((a, b) => {
                const va = (a.parameters as Record<string, unknown>)?.[key] ?? (key === 'Type' ? a.type : ''); const vb = (b.parameters as Record<string, unknown>)?.[key] ?? (key === 'Type' ? b.type : '');
                const cmp = typeof va === 'number' && typeof vb === 'number'
                    ? va - vb
                    : String(va).localeCompare(String(vb));
                return sort.ascending ? cmp : -cmp;
            });
        }
        return filtered.map((el) => ({
            id: el.id,
            values: activeSchedule.fields.map((f) => {
                const v = (el.parameters as Record<string, unknown>)?.[f.parameterName]
                    ?? (f.parameterName === 'Type' ? el.type : f.parameterName === 'Category' ? el.category : '');
                return String(v ?? '');
            }),
        }));
    }, [activeSchedule, project.elements]);

    const handleCreate = () => {
        const store = useBIMStore.getState();
        const scheduleName = name.trim() || `${category} Schedule`;
        const fieldsDef: ScheduleField[] = fields.map((f) => ({
            parameterName: f,
            heading: f,
            width: 160,
            hidden: false,
        }));
        const schedule: Schedule = {
            id: `sch-${Date.now()}`,
            name: scheduleName,
            categoryId: category,
            fields: fieldsDef,
            filters: [],
            sorting: fieldsDef.length ? [{ field: fieldsDef[0].parameterName, ascending: true }] : [],
            grouping: [],
            calculateTotals: false,
            itemizeEveryInstance: true,
        };
        createSchedule(schedule);
        // Mirror into the views registry so it shows in the Project Browser
        store.createView(scheduleName, 'Schedule');
        setName('');
        setSelectedScheduleId(schedule.id);
    };

    const toggleField = (f: string) => {
        setFields((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="3xl"
            title={t('dialogs.ui.schedulesQuantities')}
            footer={<DialogButton variant="secondary" onClick={onClose}>{t('dialogs.ui.close')}</DialogButton>}
        >
                    <Flex gap={4} align="stretch">
                        {/* Creator form */}
                        <Box w="300px" flexShrink={0}>
                            <Text fontSize="xs" fontWeight="bold" mb={1}>{t('dialogs.ui.newSchedule')}</Text>
                            <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.category')}</Text>
                            <Select size="xs" mb={2} value={category} onChange={(e) => setCategory(e.target.value)}>
                                {SCHEDULE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </Select>
                            <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.nameOptional')}</Text>
                            <Input size="xs" mb={2} placeholder={`${category} Schedule`} value={name} onChange={(e) => setName(e.target.value)} />
                            <Text fontSize="xs" color="text.secondary" mb={1}>{t('dialogs.ui.fields')}</Text>
                            <Flex direction="column" gap={1} mb={3}>
                                {DEFAULT_FIELDS.map((f) => (
                                    <Checkbox key={f} size="sm" isChecked={fields.includes(f)} onChange={() => toggleField(f)}>
                                        <Text fontSize="xs">{f}</Text>
                                    </Checkbox>
                                ))}
                            </Flex>
                            <DialogButton variant="primary" className="w-full" onClick={handleCreate} isDisabled={fields.length === 0}>{t('dialogs.ui.addSchedule')}</DialogButton>

                            <Divider my={3} />
                            <Text fontSize="xs" fontWeight="bold" mb={1}>{t('dialogs.ui.existing')}</Text>
                            {schedules.length === 0 && (
                                <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.noSchedulesYet')}</Text>
                            )}
                            {schedules.map((s) => (
                                <Flex key={s.id} align="center" gap={2} py={1} cursor="pointer"
                                    onClick={() => setSelectedScheduleId(s.id)}
                                    bg={selectedScheduleId === s.id ? 'bg.hover' : 'transparent'}
                                    px={1} borderRadius="sm">
                                    <Text fontSize="xs" flex="1" noOfLines={1}>{s.name}</Text>
                                    <Button size="xs" variant="ghost" color="var(--color-error)"
                                        onClick={(e) => { e.stopPropagation(); removeSchedule(s.id); if (selectedScheduleId === s.id) setSelectedScheduleId(null); }}>{t('dialogs.ui.del')}</Button>
                                </Flex>
                            ))}
                        </Box>

                        {/* Live table */}
                        <Box flex="1" border="1px solid" borderColor="border.base" borderRadius="sm" overflow="hidden" minH="300px">
                            {activeSchedule ? (
                                <>
                                    <Box bg="bg.toolbar" px={2} py={1}>
                                        <Text fontSize="xs" fontWeight="bold">{activeSchedule.name}</Text>
                                    </Box>
                                    <Box overflowX="auto">
                                        <Box as="table" w="100%" fontSize="xs" sx={{ borderCollapse: 'collapse' }}>
                                            <Box as="thead">
                                                <Box as="tr" bg="bg.toolbar">
                                                    {activeSchedule.fields.map((f) => (
                                                        <Box as="th" key={f.heading} textAlign="left" px={2} py={1} fontWeight="semibold">{f.heading}</Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                            <Box as="tbody">
                                                {rows.length === 0 && (
                                                    <Box as="tr">
                                                        <Box as="td" colSpan={activeSchedule.fields.length} px={2} py={4} color="text.secondary">
                                                            No {activeSchedule.categoryId} in the model yet.
                                                        </Box>
                                                    </Box>
                                                )}
                                                {rows.map((row) => (
                                                    <Box as="tr" key={row.id} _hover={{ bg: 'bg.hover' }}>
                                                        {row.values.map((v, i) => (
                                                            <Box as="td" key={i} px={2} py={1} borderTop="1px solid" borderColor="border.base">{v}</Box>
                                                        ))}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </>
                            ) : (
                                <Flex h="100%" align="center" justify="center" p={6}>
                                    <Text fontSize="xs" color="text.secondary">{t('dialogs.ui.createAScheduleOrPickAnExistingOneToSeeItsLiveTable')}</Text>
                                </Flex>
                            )}
                        </Box>
                    </Flex>
        </AppDialog>
    );
}

export default ScheduleDialog;
